<template>
  <div class="simple-swiper" :style="{ height }" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" ref="container" role="region" aria-roledescription="carousel" aria-label="Content carousel">
    <!-- Track -->
    <div class="swiper-track" :style="trackStyle" @transitionend="onTransitionEnd" @mousedown="onDragStart" @mousemove="onDragMove" @mouseup="onDragEnd" @mouseleave="onDragCancel" @touchstart.passive="onDragStart" @touchmove.prevent="onDragMove"
      @touchend="onDragEnd">
      <!-- Slides (clone cho loop) -->
      <div v-for="(item, i) in extendedItems" :key="`slide-${i}-${itemKey(item, i)}`" class="swiper-slide" :style="slideStyle" :aria-hidden="!isIndexVisible(i)">
        <!-- Scoped slot -->
        <slot name="slide" :item="item" :index="normalizeIndex(i)">
          <div class="slide-default">
            item.img
            <div class="caption" v-if="item.title">{{ item.title }}</div>
          </div>
        </slot>
      </div>
    </div>

    <!-- Navigation -->
    <button v-if="showNavigation" class="nav-btn prev" @click="prev" aria-label="Previous">
      ‹
    </button>
    <button v-if="showNavigation" class="nav-btn next" @click="next" aria-label="Next">
      ›
    </button>

    <!-- Pagination (auto ẩn nếu scrollbar hiển thị) -->
    <div v-if="computedShowPagination" class="pagination" role="tablist">
      <button v-for="i in pageCount" :key="`dot-${i - 1}`" class="dot" :class="{ active: currentPage === (i - 1) }" @click="goToPage(i - 1)" role="tab" :aria-selected="currentPage === (i - 1)" :aria-label="`Go to group ${i}`"></button>
    </div>

    <!-- Scrollbar (auto toggle với pagination) -->
    <div v-if="computedShowScrollbar" class="swiper-scrollbar" :style="scrollbarContainerStyle" ref="scrollbarTrack" @mousedown="onScrollbarDown" @touchstart.passive="onScrollbarDown" @click="onScrollbarClick" aria-label="Carousel scrollbar"
      role="scrollbar">
      <div class="scrollbar-track" :style="scrollbarTrackStyle">
        <div class="scrollbar-thumb" :style="scrollbarThumbStyle" @mousedown.stop="onScrollbarDown" @touchstart.stop.passive="onScrollbarDown"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SimpleSwiper',

  props: {
    items: { type: Array, required: true },

    // Index khởi tạo (index gốc, không tính clone)
    initialIndex: { type: Number, default: 0 },

    // Hiển thị/di chuyển nhiều slide
    slidesPerView: { type: Number, default: 1 },
    slidesPerGroup: { type: Number, default: 1 },
    spaceBetween: { type: Number, default: 0 },

    // Responsive theo chiều rộng container
    // { width: { slidesPerView, slidesPerGroup, spaceBetween } }
    breakpoints: { type: Object, default: () => ({}) },

    loop: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: true },
    autoplayDelay: { type: Number, default: 3000 },
    transitionDuration: { type: Number, default: 300 },
    transitionEasing: { type: String, default: 'cubic-bezier(0.22, 1, 0.36, 1)' }, // ease-out mềm
    threshold: { type: Number, default: 0.15 }, // theo tỉ lệ slideWidth
    draggable: { type: Boolean, default: true },
    pauseOnHover: { type: Boolean, default: true },
    showNavigation: { type: Boolean, default: true },
    showPagination: { type: Boolean, default: true },
    height: { type: String, default: 'auto' },
    keyboard: { type: Boolean, default: true },

    // Scrollbar
    showScrollbar: { type: Boolean, default: true },
    scrollbarDraggable: { type: Boolean, default: true },
    scrollbarHeight: { type: Number, default: 4 },
    scrollbarPadding: { type: Number, default: 8 },
    scrollbarTrackColor: { type: String, default: 'rgba(255,255,255,0.25)' },
    scrollbarThumbColor: { type: String, default: '#fff' },
    scrollbarRadius: { type: Number, default: 4 },
    scrollbarMinThumbSize: { type: Number, default: 20 },
    scrollbarBottomOffset: { type: Number, default: 0 },

    // Hiển thị một phần slide tiếp theo (px)
    peekNext: { type: Number, default: 24 },

    // Mượt khi kéo scrollbar: dùng float liên tục, snap khi thả
    scrollbarSmooth: { type: Boolean, default: true },
    snapOnScrollbarRelease: { type: Boolean, default: true }, // snap theo group khi thả
  },

  data() {
    return {
      containerWidth: 0,

      // cấu hình theo breakpoints
      activeSlidesPerView: this.slidesPerView,
      activeSlidesPerGroup: this.slidesPerGroup,
      activeSpaceBetween: this.spaceBetween,

      // index (extended) của slide bên trái viewport khi di chuyển theo step
      currentIndex: 0,
      isAnimating: false,

      // Drag state (swiper track)
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragDeltaX: 0,
      hasMovedHorizontally: false,

      // Autoplay
      timerId: null,
      isPausedByHover: false,

      // Nội bộ
      internalTransition: this.transitionDuration,

      // Scrollbar dragging
      sbDragging: false,
      sbTrackRect: null,
      sbThumbOffsetX: 0,

      // Float vị trí bắt đầu (original start) khi kéo scrollbar (mượt hơn)
      startFloatOriginal: 0, // 0..(n - slidesPerView)
    }
  },

  computed: {
    // —— Auto toggle Pagination & Scrollbar —— //
    canScroll() { return this.items.length > this.activeSlidesPerView },
    computedShowScrollbar() { return this.showScrollbar && this.canScroll },
    computedShowPagination() { return this.showPagination && !this.computedShowScrollbar },

    // số clone mỗi đầu để loop mượt
    cloneCount() {
      if (!this.loop || this.items.length <= 1) return 0
      return Math.max(this.activeSlidesPerView, this.activeSlidesPerGroup)
    },

    extendedItems() {
      if (!this.loop || this.items.length <= 1) return this.items
      const n = this.items.length
      if (n === 0) return []
      const head = []
      const tail = []
      for (let i = 0; i < this.cloneCount; i++) {
        head.push(this.items[i % n]) // clone đầu
        tail.push(this.items[(n - this.cloneCount + i + n) % n]) // clone cuối
      }
      return [...tail, ...this.items, ...head]
    },

    maxStartIndex() {
      const len = this.extendedItems.length
      return Math.max(0, len - this.activeSlidesPerView)
    },

    // —— Peek logic —— //
    hasMoreAfter() {
      const n = this.items.length
      const sv = this.activeSlidesPerView
      if (n <= sv) return false
      const originalStart = this.normalizeIndex(this.currentIndex)
      return originalStart < (n - sv)
    },

    currentShouldShowPeek() {
      if (this.isSingleSlide) return false
      return this.peekNext > 0 && (this.loop || this.hasMoreAfter)
    },

    effectiveContainerWidth() {
      const peek = this.currentShouldShowPeek ? this.peekNext : 0
      return Math.max(0, this.containerWidth - peek)
    },

    // —— Kích thước theo px —— //
    slideWidthPx() {
      const sv = Math.max(1, this.activeSlidesPerView)
      const totalGap = this.activeSpaceBetween * (sv - 1)
      const raw = sv > 0 ? (this.effectiveContainerWidth - totalGap) / sv : this.effectiveContainerWidth
      return Math.max(0, raw)
    },
    stepPx() {
      return this.slideWidthPx + this.activeSpaceBetween
    },

    trackStyle() {
      const baseStart = this.sbDragging && this.scrollbarSmooth
        ? (this.loop ? (this.cloneCount + this.startFloatOriginal) : this.startFloatOriginal)
        : this.currentIndex

      const translateX = -(baseStart * this.stepPx) + (this.isDragging ? this.dragDeltaX : 0)

      return {
        transform: `translate3d(${translateX}px, 0, 0)`,
        transition: this.isAnimating ? `transform ${this.internalTransition}ms ${this.transitionEasing}` : 'none',
        willChange: 'transform',
        display: 'flex',
        gap: this.isSingleSlide ? '0px' : `${this.activeSpaceBetween}px`,
        height: '100%',
        cursor: this.draggable ? 'grab' : 'default',
        userSelect: 'none',
      }
    },



    slideStyle() {
      if (this.isSingleSlide) {
        return {
          flex: '0 0 100%',
          width: '100%',
          boxSizing: 'border-box',
          height: '100%',
          position: 'relative',
        }
      }
      return {
        flex: `0 0 ${this.slideWidthPx}px`,
        width: `${this.slideWidthPx}px`,
        boxSizing: 'border-box',
        height: '100%',
        position: 'relative',
      }
    },


    // —— Pagination theo nhóm —— //
    pageCount() {
      const n = this.items.length
      const group = Math.max(1, this.activeSlidesPerGroup)
      return Math.max(1, Math.ceil(n / group))
    },
    currentPage() {
      const realIdx = this.normalizeIndex(this.currentIndex)
      const group = Math.max(1, this.activeSlidesPerGroup)
      return Math.floor(realIdx / group)
    },

    // —— Scrollbar computed —— //
    sbTrackWidth() { return Math.max(0, this.containerWidth - 2 * this.scrollbarPadding) },
    sbVisibleCount() { return Math.min(this.items.length, this.activeSlidesPerView) },
    sbTotalCount() { return Math.max(1, this.items.length) },
    sbRange() { return Math.max(0, this.items.length - this.sbVisibleCount) },

    sbThumbWidthPx() {
      const size = this.sbTrackWidth * (this.sbVisibleCount / this.sbTotalCount)
      return Math.max(this.scrollbarMinThumbSize, Math.min(size, this.sbTrackWidth))
    },

    // Vị trí thumb: dùng float khi đang kéo để phản hồi mượt
    sbCurrentOriginalStart() {
      const n = this.items.length
      const vis = this.sbVisibleCount
      if (n <= vis) return 0
      return Math.min(this.normalizeIndex(this.currentIndex), n - vis)
    },
    sbCurrentOriginalStartFloat() {
      if (!this.sbDragging || !this.scrollbarSmooth) return this.sbCurrentOriginalStart
      return Math.max(0, Math.min(this.startFloatOriginal, this.sbRange))
    },
    sbThumbLeftPx() {
      const range = this.sbRange
      if (range <= 0) return 0
      const ratio = this.sbCurrentOriginalStartFloat / range
      const maxLeft = Math.max(0, this.sbTrackWidth - this.sbThumbWidthPx)
      return ratio * maxLeft
    },

    scrollbarContainerStyle() {
      return {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: `${this.scrollbarBottomOffset}px`,
        padding: `0 ${this.scrollbarPadding}px`,
        zIndex: 9, // thấp hơn pagination (10)
      }
    },
    scrollbarTrackStyle() {
      return {
        width: '100%',
        height: `${this.scrollbarHeight}px`,
        background: this.scrollbarTrackColor,
        borderRadius: `${this.scrollbarRadius}px`,
        position: 'relative',
      }
    },
    scrollbarThumbStyle() {
      return {
        position: 'absolute',
        height: `${this.scrollbarHeight}px`,
        width: `${this.sbThumbWidthPx}px`,
        left: `${this.sbThumbLeftPx}px`,
        top: 0,
        background: this.scrollbarThumbColor,
        borderRadius: `${this.scrollbarRadius}px`,
        cursor: this.scrollbarDraggable ? 'grab' : 'default',
      }
    },

    isSingleSlide() {
      return this.items.length <= 1
    },

  },

  watch: {
    items: {
      handler() {
        // data thay đổi: đảm bảo index/layout hợp lệ
        this.resetToInitial()
        this.clampIndexForLayout()
        this.syncFloatFromCurrent()
      },
      deep: true,
    },
  },

  mounted() {
    this.updateWidth()
    window.addEventListener('resize', this.updateWidth, { passive: true })
    this.applyActiveConfig()
    this.resetToInitial()
    this.syncFloatFromCurrent()
    if (this.autoplay) this.startAutoplay()
    if (this.keyboard) window.addEventListener('keydown', this.onKeyDown)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidth)
    window.removeEventListener('keydown', this.onKeyDown)
    this.stopAutoplay()
    this.teardownScrollbarDrag()
  },

  methods: {
    // ——— Utility ———
    itemKey(item, i) { return item && item.id ? item.id : i },
    isIndexVisible(i) { return i >= this.currentIndex && i < this.currentIndex + this.activeSlidesPerView },

    normalizeIndex(extendedIndex) {
      // extendedIndex (bên trái viewport) -> index gốc
      if (!this.loop || this.items.length <= 1)
        return Math.max(0, Math.min(extendedIndex, this.items.length - 1))
      const n = this.items.length
      const base = extendedIndex - this.cloneCount
      return ((base % n) + n) % n
    },
    clampStartIndex(idx) {
      return Math.max(0, Math.min(idx, this.maxStartIndex))
    },

    updateWidth() {
      this.$nextTick(() => {
        const el = this.$refs.container
        this.containerWidth = el ? el.clientWidth : 0
        this.applyActiveConfig()
        this.clampIndexForLayout()
        this.syncFloatFromCurrent()
      })
    },

    applyActiveConfig() {
      // áp breakpoints theo containerWidth
      const width = this.containerWidth
      const keys = Object.keys(this.breakpoints)
        .map(k => Number(k))
        .filter(k => !isNaN(k))
        .sort((a, b) => a - b)

      let conf = null
      for (const k of keys) if (width >= k) conf = this.breakpoints[k]

      if (conf) {
        this.activeSlidesPerView = Math.max(1, conf.slidesPerView || this.slidesPerView)
        this.activeSlidesPerGroup = Math.max(1, conf.slidesPerGroup || this.slidesPerGroup)
        this.activeSpaceBetween = Math.max(0, conf.spaceBetween != null ? conf.spaceBetween : this.spaceBetween)
      } else {
        this.activeSlidesPerView = Math.max(1, this.slidesPerView)
        this.activeSlidesPerGroup = Math.max(1, this.slidesPerGroup)
        this.activeSpaceBetween = Math.max(0, this.spaceBetween)
      }
    },

    clampIndexForLayout() {
      // Khi layout thay đổi (SV/peek), đảm bảo currentIndex không vượt giới hạn
      this.currentIndex = this.clampStartIndex(this.currentIndex)
    },

    syncFloatFromCurrent() {
      // Đồng bộ vị trí float từ currentIndex (khi không kéo scrollbar)
      const n = this.items.length
      const vis = this.sbVisibleCount
      if (n <= vis) { this.startFloatOriginal = 0; return }
      this.startFloatOriginal = Math.min(this.normalizeIndex(this.currentIndex), n - vis)
    },

    resetToInitial() {
      const n = this.items.length
      if (n === 0) { this.currentIndex = 0; return }
      if (this.loop && n > 1) {
        const start = this.cloneCount + Math.min(this.initialIndex, n - this.activeSlidesPerView)
        this.currentIndex = this.clampStartIndex(start)
      } else {
        this.currentIndex = this.clampStartIndex(Math.min(this.initialIndex, n - this.activeSlidesPerView))
      }
    },

    // ——— Navigation ———
    goToPage(pageIdx) {
      const n = this.items.length
      const group = this.activeSlidesPerGroup
      const targetOriginalStart = Math.min(pageIdx * group, Math.max(0, n - this.activeSlidesPerView))
      const targetExtendedStart = this.loop ? this.cloneCount + targetOriginalStart : targetOriginalStart
      this.isAnimating = true
      this.internalTransition = this.transitionDuration
      this.currentIndex = this.clampStartIndex(targetExtendedStart)
      this.afterUserInteracted()
      this.syncFloatFromCurrent()
    },
    next() {
      const step = this.activeSlidesPerGroup
      this.isAnimating = true
      this.internalTransition = this.transitionDuration
      this.currentIndex = this.clampStartIndex(this.currentIndex + step)
      this.afterUserInteracted()
      this.syncFloatFromCurrent()
    },
    prev() {
      const step = this.activeSlidesPerGroup
      this.isAnimating = true
      this.internalTransition = this.transitionDuration
      this.currentIndex = this.clampStartIndex(this.currentIndex - step)
      this.afterUserInteracted()
      this.syncFloatFromCurrent()
    },
    onTransitionEnd() {
      if (this.loop && this.items.length > 1) {
        const n = this.items.length
        if (this.currentIndex < this.cloneCount) {
          this.isAnimating = false
          this.internalTransition = 0
          this.currentIndex += n
        } else if (this.currentIndex >= this.cloneCount + n) {
          this.isAnimating = false
          this.internalTransition = 0
          this.currentIndex -= n
        }
      }
      // sau khi teleport / chuyển xong: đồng bộ float
      this.syncFloatFromCurrent()
    },

    // ——— Dragging (swiper track) ———
    getPoint(e) {
      if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
      return { x: e.clientX, y: e.clientY }
    },
    onDragStart(e) {
      if (!this.draggable || this.items.length <= 1) return
      this.isDragging = true
      this.isAnimating = false
      this.internalTransition = 0
      const p = this.getPoint(e)
      this.dragStartX = p.x
      this.dragStartY = p.y
      this.dragDeltaX = 0
      this.hasMovedHorizontally = false
      this.afterUserInteracted()
    },
    onDragMove(e) {
      if (!this.isDragging) return
      const p = this.getPoint(e)
      const dx = p.x - this.dragStartX
      const dy = p.y - this.dragStartY
      if (!this.hasMovedHorizontally) {
        if (Math.abs(dy) > Math.abs(dx)) { this.onDragCancel(); return }
        else { this.hasMovedHorizontally = true }
      }
      this.dragDeltaX = dx
    },
    onDragEnd() {
      if (!this.isDragging) return
      this.isDragging = false
      const ratio = Math.abs(this.dragDeltaX) / (this.slideWidthPx || 1)
      this.isAnimating = true
      this.internalTransition = this.transitionDuration
      if (ratio > this.threshold) {
        const step = this.activeSlidesPerGroup
        if (this.dragDeltaX < 0) this.currentIndex = this.clampStartIndex(this.currentIndex + step)
        else this.currentIndex = this.clampStartIndex(this.currentIndex - step)
      }
      this.dragDeltaX = 0
      this.syncFloatFromCurrent()
    },
    onDragCancel() {
      if (!this.isDragging) return
      this.isDragging = false
      this.isAnimating = true
      this.internalTransition = this.transitionDuration
      this.dragDeltaX = 0
      this.syncFloatFromCurrent()
    },

    // ——— Autoplay ———
    startAutoplay() {
      this.stopAutoplay()
      if (!this.autoplay || this.items.length <= 1) return
      this.timerId = setInterval(() => {
        if (this.isPausedByHover || this.isDragging || this.sbDragging) return
        this.next()
      }, this.autoplayDelay)
    },
    stopAutoplay() {
      if (this.timerId) { clearInterval(this.timerId); this.timerId = null }
    },
    afterUserInteracted() {
      this.stopAutoplay()
      if (this.autoplay) setTimeout(() => this.startAutoplay(), this.autoplayDelay)
    },
    handleMouseEnter() { if (this.pauseOnHover) this.isPausedByHover = true },
    handleMouseLeave() { this.isPausedByHover = false },

    // ——— Keyboard ———
    onKeyDown(e) {
      if (!this.keyboard) return
      if (e.key === 'ArrowRight') this.next()
      else if (e.key === 'ArrowLeft') this.prev()
    },

    // ——— Scrollbar interactions (mượt) ———
    getClientX(e) {
      return e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX
    },
    setupScrollbarDrag() {
      const move = this.onScrollbarMove
      const up = this.onScrollbarUp
      window.addEventListener('mousemove', move, { passive: false })
      window.addEventListener('mouseup', up, { passive: true })
      window.addEventListener('touchmove', move, { passive: false })
      window.addEventListener('touchend', up, { passive: true })
    },
    teardownScrollbarDrag() {
      const move = this.onScrollbarMove
      const up = this.onScrollbarUp
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    },
    onScrollbarDown(e) {
      if (!this.scrollbarDraggable || !this.canScroll) return
      this.sbDragging = true
      this.isAnimating = false
      this.internalTransition = 0
      this.afterUserInteracted()
      const track = this.$refs.scrollbarTrack
      if (!track) return
      this.sbTrackRect = track.getBoundingClientRect()
      const x = this.getClientX(e)
      const localX = x - this.sbTrackRect.left
      // nếu mousedown trên thumb: giữ offset để kéo mượt
      this.sbThumbOffsetX = Math.max(0, localX - this.sbThumbLeftPx)
      this.setupScrollbarDrag()
      // cập nhật vị trí ngay lập tức
      this.updateScrollbarFloat(localX - this.sbThumbOffsetX)
    },
    onScrollbarMove(e) {
      if (!this.sbDragging || !this.sbTrackRect) return
      e.preventDefault()
      const x = this.getClientX(e)
      const localX = x - this.sbTrackRect.left - this.sbThumbOffsetX
      this.updateScrollbarFloat(localX)
    },
    onScrollbarUp() {
      if (!this.sbDragging) return
      this.sbDragging = false
      this.teardownScrollbarDrag()

      // Khi thả: snap theo group (mềm bằng transition)
      if (this.snapOnScrollbarRelease) {
        const g = Math.max(1, this.activeSlidesPerGroup)
        const n = this.items.length
        const vis = this.sbVisibleCount
        if (n > vis) {
          let targetOriginalStart = Math.round(this.startFloatOriginal / g) * g
          targetOriginalStart = Math.min(targetOriginalStart, Math.max(0, n - vis))
          const targetExtendedStart = this.loop ? (this.cloneCount + targetOriginalStart) : targetOriginalStart
          this.isAnimating = true
          this.internalTransition = this.transitionDuration
          this.currentIndex = this.clampStartIndex(targetExtendedStart)
          this.syncFloatFromCurrent()
        }
      } else {
        // Không snap: đồng bộ float với vị trí hiện tại để giữ nguyên
        this.syncFloatFromCurrent()
      }
    },
    onScrollbarClick(e) {
      if (!this.canScroll) return
      const track = this.$refs.scrollbarTrack
      if (!track) return
      const rect = track.getBoundingClientRect()
      const x = this.getClientX(e)
      const localX = x - rect.left - this.sbThumbWidthPx / 2
      // click track: cập nhật float, rồi snap nhẹ về group
      this.updateScrollbarFloat(localX)
      if (this.snapOnScrollbarRelease) {
        const g = Math.max(1, this.activeSlidesPerGroup)
        const n = this.items.length
        const vis = this.sbVisibleCount
        let targetOriginalStart = Math.round(this.startFloatOriginal / g) * g
        targetOriginalStart = Math.min(targetOriginalStart, Math.max(0, n - vis))
        const targetExtendedStart = this.loop ? (this.cloneCount + targetOriginalStart) : targetOriginalStart
        this.isAnimating = true
        this.internalTransition = this.transitionDuration
        this.currentIndex = this.clampStartIndex(targetExtendedStart)
        this.afterUserInteracted()
        this.syncFloatFromCurrent()
      }
    },
    updateScrollbarFloat(localLeft) {
      // chuyển vị trí thumb -> float start (original), mượt liên tục
      const maxLeft = Math.max(0, this.sbTrackWidth - this.sbThumbWidthPx)
      const clampedLeft = Math.max(0, Math.min(localLeft, maxLeft))
      const range = this.sbRange
      const ratio = maxLeft > 0 ? clampedLeft / maxLeft : 0
      this.startFloatOriginal = range * ratio
      // Không bật transition trong lúc kéo
      this.isAnimating = false
      this.internalTransition = 0
    },
  },
}
</script>

<style scoped>
.simple-swiper {
  position: relative;
  width: 100%;
  overflow: hidden;
}

/* Track & Slide */
.swiper-track {
  height: 100%;
}

.swiper-slide {
  position: relative;
}

/* Fallback content */
.slide-default {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.slide-default img {
  width: 100%;
  height: auto;
  object-fit: cover;
  pointer-events: none;
}

.slide-default .caption {
  text-align: center;
  padding: 8px;
  font-size: 14px;
}

/* Navigation buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  font-size: 22px;
  line-height: 36px;
  text-align: center;
  cursor: pointer;
}

.nav-btn.prev {
  left: 8px;
}

.nav-btn.next {
  right: 8px;
}

.nav-btn:hover {
  background: rgba(0, 0, 0, 0.6);
}

/* Pagination dots (auto ẩn nếu scrollbar hiển thị) */
.pagination {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 44px;
  /* nhường chỗ cho scrollbar khi cần */
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 10;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
}

.dot.active {
  background: #fff;
}

/* Scrollbar */
.swiper-scrollbar {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 9;
}

.scrollbar-track {
  width: 100%;
  position: relative;
}

.scrollbar-thumb {
  position: absolute;
  top: 0;
}
</style>